import { useCallback, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { createCardOrder, finaliseCardOrder } from './cardPaymentApi';
import { GENERAL_PAYMENT_ERROR } from './cardPaymentErrors';
import { runCardPayment } from './runCardPayment';
import {
  CardOrderRequest,
  CardOrderRole,
  CardPaymentOutcome,
  CardPaymentStage,
  OpenCardOrder,
} from './types';

export type CardPaymentHookStage = CardPaymentStage | 'done';

/**
 * The only place the portal talks to Stripe. Must be rendered inside CardPaymentElements, with a
 * CardElement mounted whenever the total is above zero.
 */
export const useCardPayment = (role: CardOrderRole) => {
  const stripe = useStripe();
  const elements = useElements();
  const [stage, setStage] = useState<CardPaymentHookStage>('idle');
  const openOrder = useRef<OpenCardOrder | null>(null);
  const busy = useRef(false);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const isPaying = stage === 'creating' || stage === 'confirming' || stage === 'finalising';

  useEffect(() => {
    if (!isPaying) return;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isPaying]);

  /**
   * Takes the payment and calls onOutcome in the same render as the processing state ends, so the page
   * never shows the form again between paying and its result.
   */
  const pay = useCallback(
    async (request: CardOrderRequest, cardholderName: string, onOutcome: (outcome: CardPaymentOutcome) => void) => {
      if (busy.current) return;
      busy.current = true;

      let outcome: CardPaymentOutcome;
      const card = elements && elements.getElement(CardElement);
      // The API applies a zero total without taking a payment, so no card is needed.
      const bNeedsCard = request.expectedGrossAmount > 0;

      if (bNeedsCard && (!stripe || !card)) {
        outcome = { kind: 'failed', message: GENERAL_PAYMENT_ERROR };
      } else {
        try {
          const run = await runCardPayment(request, openOrder.current, {
            createOrder: (orderRequest) => createCardOrder(role, orderRequest),
            confirmPayment: (clientSecret) =>
              stripe && card
                ? stripe.confirmCardPayment(clientSecret, {
                    payment_method: { card, billing_details: { name: cardholderName } },
                  })
                : Promise.resolve({ error: { message: GENERAL_PAYMENT_ERROR } }),
            finalise: (orderReference) => finaliseCardOrder(role, orderReference),
            wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
            onStage: (next) => mounted.current && setStage(next),
          });

          openOrder.current = run.openOrder;
          outcome = run.outcome;
        } catch {
          // Only Stripe.js can throw here, before any payment. A fresh order next time is safe:
          // the server cancels this one, or reports it if it was paid after all.
          openOrder.current = null;
          outcome = { kind: 'failed', message: GENERAL_PAYMENT_ERROR };
        }
      }

      busy.current = false;

      if (!mounted.current) return;

      unstable_batchedUpdates(() => {
        setStage(outcome.kind === 'failed' ? 'idle' : 'done');
        onOutcome(outcome);
      });
    },
    [role, stripe, elements]
  );

  return { pay, stage, isPaying };
};
